
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from '@/components/compat/Link';
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  CircleAlert,
  ExternalLink,
  Lightbulb,
  MessagesSquare,
  Search,
  Server,
  Ticket,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  getXatErrorsData,
  SEVERITY_LABELS,
  type XatErrorCategory,
  type XatErrorCategoryId,
  type XatErrorCallout,
  type XatErrorEntry,
  type XatErrorSeverity,
} from '@/lib/xat-errors-data';
import { getGuideHref } from '@/lib/guide-routes';
import { UserNavIcon } from '@/components/help-center/MaskedSvgIcon';
import { cn } from '@/lib/utils';

const CATEGORY_ICONS: Record<XatErrorCategoryId, LucideIcon> = {
  conta: UserNavIcon,
  sistema: Server,
  grupos: MessagesSquare,
  tickets: Ticket,
};

const CATEGORY_THEMES: Record<
  XatErrorCategoryId,
  {
    card: {
      iconBg: string;
      iconText: string;
      hoverBorder: string;
      countText: string;
      focusRing: string;
    };
    /** Cards resumo em night mode — cores do shell navy dos cards da homepage. */
    nightCard: {
      iconBg: string;
      iconText: string;
      hoverBorder: string;
      hoverShadow: string;
      countText: string;
      focusRing: string;
    };
    codeBadge: {
      border: string;
      bg: string;
      text: string;
    };
  }
> = {
  conta: {
    card: {
      iconBg: 'bg-blue-50 group-hover:bg-blue-100',
      iconText: 'text-blue-600',
      hoverBorder: 'hover:border-blue-200',
      countText: 'text-blue-600',
      focusRing: 'focus-visible:ring-blue-500',
    },
    nightCard: {
      iconBg: 'bg-blue-500/15 ring-1 ring-blue-400/25 group-hover:bg-blue-500/25',
      iconText: 'text-blue-300',
      hoverBorder: 'hover:border-blue-400/45 hover:ring-blue-400/30',
      hoverShadow:
        'hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(96,165,250,0.35)]',
      countText: 'text-blue-300',
      focusRing: 'focus-visible:ring-blue-400',
    },
    codeBadge: {
      border: 'border-sky-100',
      bg: 'bg-gradient-to-b from-white to-sky-50/80',
      text: 'text-sky-900',
    },
  },
  sistema: {
    card: {
      iconBg: 'bg-red-50 group-hover:bg-red-100',
      iconText: 'text-red-600',
      hoverBorder: 'hover:border-red-200',
      countText: 'text-red-600',
      focusRing: 'focus-visible:ring-red-500',
    },
    nightCard: {
      iconBg: 'bg-red-500/15 ring-1 ring-red-400/25 group-hover:bg-red-500/25',
      iconText: 'text-red-300',
      hoverBorder: 'hover:border-red-400/45 hover:ring-red-400/30',
      hoverShadow:
        'hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(248,113,113,0.32)]',
      countText: 'text-red-300',
      focusRing: 'focus-visible:ring-red-400',
    },
    codeBadge: {
      border: 'border-red-100',
      bg: 'bg-gradient-to-b from-white to-red-50/80',
      text: 'text-red-900',
    },
  },
  grupos: {
    card: {
      iconBg: 'bg-emerald-50 group-hover:bg-emerald-100',
      iconText: 'text-emerald-600',
      hoverBorder: 'hover:border-emerald-200',
      countText: 'text-emerald-600',
      focusRing: 'focus-visible:ring-emerald-500',
    },
    nightCard: {
      iconBg: 'bg-emerald-500/15 ring-1 ring-emerald-400/25 group-hover:bg-emerald-500/25',
      iconText: 'text-emerald-300',
      hoverBorder: 'hover:border-emerald-400/45 hover:ring-emerald-400/30',
      hoverShadow:
        'hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(52,211,153,0.32)]',
      countText: 'text-emerald-300',
      focusRing: 'focus-visible:ring-emerald-400',
    },
    codeBadge: {
      border: 'border-emerald-100',
      bg: 'bg-gradient-to-b from-white to-emerald-50/80',
      text: 'text-emerald-900',
    },
  },
  tickets: {
    card: {
      iconBg: 'bg-amber-50 group-hover:bg-amber-100',
      iconText: 'text-amber-600',
      hoverBorder: 'hover:border-amber-200',
      countText: 'text-amber-600',
      focusRing: 'focus-visible:ring-amber-500',
    },
    nightCard: {
      iconBg: 'bg-amber-500/15 ring-1 ring-amber-400/25 group-hover:bg-amber-500/25',
      iconText: 'text-amber-300',
      hoverBorder: 'hover:border-amber-400/45 hover:ring-amber-400/30',
      hoverShadow:
        'hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(251,191,36,0.32)]',
      countText: 'text-amber-300',
      focusRing: 'focus-visible:ring-amber-400',
    },
    codeBadge: {
      border: 'border-amber-100',
      bg: 'bg-gradient-to-b from-white to-amber-50/80',
      text: 'text-amber-900',
    },
  },
};

const SEVERITY_STYLES: Record<
  XatErrorSeverity,
  { badge: string; dot: string }
> = {
  info: {
    badge: 'bg-blue-50 text-blue-700 border-blue-100',
    dot: 'bg-blue-500',
  },
  warning: {
    badge: 'bg-amber-50 text-amber-800 border-amber-100',
    dot: 'bg-amber-500',
  },
  error: {
    badge: 'bg-red-50 text-red-700 border-red-100',
    dot: 'bg-red-500',
  },
  critical: {
    badge: 'bg-rose-50 text-rose-800 border-rose-100',
    dot: 'bg-rose-600',
  },
};

const ERROR_SCROLL_OFFSET = 112;

function normalizeSearchQuery(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, ' ');
}

function matchesSearch(entry: XatErrorEntry, query: string): boolean {
  if (!query) return true;
  const haystack = entry.searchText;
  const tokens = query.split(/\s+/).filter(Boolean);
  return tokens.every((token) => haystack.includes(token));
}

const CALLOUT_THEMES: Record<
  XatErrorCallout['type'],
  {
    icon: typeof CircleAlert;
    label: string;
    accentBar: string;
    cardBorder: string;
    cardBg: string;
    iconShell: string;
    badge: string;
    titleColor: string;
    textColor: string;
    shadow: string;
  }
> = {
  info: {
    icon: CircleAlert,
    label: 'Informação',
    accentBar: 'bg-blue-500',
    cardBorder: 'border-blue-100/80',
    cardBg: 'bg-gradient-to-br from-white to-blue-50/40',
    iconShell: 'bg-blue-50 text-blue-600 ring-blue-100/80',
    badge: 'border-blue-100 bg-blue-50 text-blue-700',
    titleColor: 'text-blue-950',
    textColor: 'text-slate-600',
    shadow: 'shadow-[0_8px_30px_-12px_rgba(59,130,246,0.35)]',
  },
  tip: {
    icon: Lightbulb,
    label: 'Dica',
    accentBar: 'bg-emerald-500',
    cardBorder: 'border-emerald-100/80',
    cardBg: 'bg-gradient-to-br from-white to-emerald-50/50',
    iconShell: 'bg-emerald-50 text-emerald-600 ring-emerald-100/80',
    badge: 'border-emerald-100 bg-emerald-50 text-emerald-800',
    titleColor: 'text-emerald-950',
    textColor: 'text-slate-600',
    shadow: 'shadow-[0_8px_30px_-12px_rgba(16,185,129,0.3)]',
  },
  warning: {
    icon: AlertTriangle,
    label: 'Atenção',
    accentBar: 'bg-amber-500',
    cardBorder: 'border-amber-100/80',
    cardBg: 'bg-gradient-to-br from-white to-amber-50/45',
    iconShell: 'bg-amber-50 text-amber-600 ring-amber-100/80',
    badge: 'border-amber-100 bg-amber-50 text-amber-800',
    titleColor: 'text-amber-950',
    textColor: 'text-slate-600',
    shadow: 'shadow-[0_8px_30px_-12px_rgba(245,158,11,0.32)]',
  },
  success: {
    icon: Lightbulb,
    label: 'Resolvido',
    accentBar: 'bg-teal-500',
    cardBorder: 'border-teal-100/80',
    cardBg: 'bg-gradient-to-br from-white to-teal-50/45',
    iconShell: 'bg-teal-50 text-teal-600 ring-teal-100/80',
    badge: 'border-teal-100 bg-teal-50 text-teal-800',
    titleColor: 'text-teal-950',
    textColor: 'text-slate-600',
    shadow: 'shadow-[0_8px_30px_-12px_rgba(20,184,166,0.3)]',
  },
};

function ErrorCallout({ callout }: { callout: XatErrorCallout }) {
  const theme = CALLOUT_THEMES[callout.type];
  const Icon = theme.icon;
  const title = callout.title || theme.label;

  return (
    <article
      className={cn(
        'group relative overflow-hidden rounded-2xl border transition-all duration-300',
        'hover:-translate-y-0.5 hover:shadow-lg',
        theme.cardBorder,
        theme.cardBg,
        theme.shadow
      )}
    >
      <div
        className={cn('absolute inset-y-0 left-0 w-1', theme.accentBar)}
        aria-hidden
      />
      <div className="flex h-full flex-col p-5 pl-6 md:p-6 md:pl-7">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 transition-transform duration-300 group-hover:scale-105',
              theme.iconShell
            )}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <span
            className={cn(
              'inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5',
              'font-inter text-[11px] font-semibold uppercase tracking-wide',
              theme.badge
            )}
          >
            {theme.label}
          </span>
        </div>
        <h3 className={cn('mb-2 font-poppins text-lg font-bold leading-snug', theme.titleColor)}>
          {title}
        </h3>
        <p className={cn('flex-1 font-inter text-[15px] leading-relaxed', theme.textColor)}>
          {callout.content}
        </p>
      </div>
    </article>
  );
}

function CategorySummaryCard({
  category,
  onNavigate,
}: {
  category: XatErrorCategory;
  onNavigate: (id: XatErrorCategoryId) => void;
}) {
  const Icon = CATEGORY_ICONS[category.id];
  const theme = CATEGORY_THEMES[category.id].nightCard;

  return (
    <button
      type="button"
      onClick={() => onNavigate(category.id)}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-blue-900/60 bg-[#030023] p-5 text-left',
        'shadow-lg shadow-black/25 ring-1 ring-sky-500/10',
        'transition-[box-shadow,border-color,ring-color,transform] duration-300 ease-out',
        'hover:-translate-y-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]',
        theme.hoverBorder,
        theme.hoverShadow,
        theme.focusRing
      )}
    >
      {/* Degradês do shell navy — mesmos dos cards de categoria da homepage */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#06083a] via-[#04042e] to-[#030023]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#000b6a]/40 via-transparent to-[#01012e]"
        aria-hidden
      />
      {/* Hover: azul mais claro na mesma direção do degradê (igual homepage) */}
      <span
        className={cn(
          'pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300',
          'bg-gradient-to-br from-[#000b6a] via-[#000b6a]/35 to-transparent',
          'group-hover:opacity-100'
        )}
        aria-hidden
      />
      <div
        className={cn(
          'relative z-10 mb-4 flex h-11 w-11 items-center justify-center rounded-xl transition-colors',
          theme.iconBg,
          theme.iconText
        )}
      >
        <Icon
          className={cn(category.id === 'conta' ? 'h-7 w-7' : 'h-5 w-5')}
          aria-hidden
        />
      </div>
      <h3 className="relative z-10 mb-1.5 font-poppins text-lg font-bold text-white transition-colors duration-300 group-hover:text-sky-100">
        {category.shortTitle}
      </h3>
      <p className="relative z-10 mb-4 flex-1 font-inter text-sm leading-relaxed text-blue-100/75 transition-colors duration-300 group-hover:text-blue-100/90">
        {category.shortDescription}
      </p>
      <p className={cn('relative z-10 font-inter text-sm font-semibold', theme.countText)}>
        {category.errors.length} {category.errors.length === 1 ? 'erro' : 'erros'}
      </p>
    </button>
  );
}

function ErrorAccordionItem({
  entry,
  isOpen,
  onToggle,
  onOpenRelated,
  allEntriesById,
}: {
  entry: XatErrorEntry;
  isOpen: boolean;
  onToggle: () => void;
  onOpenRelated: (id: string) => void;
  allEntriesById: Map<string, XatErrorEntry>;
}) {
  const severity = SEVERITY_STYLES[entry.severity];
  const codeTheme = CATEGORY_THEMES[entry.categoryId].codeBadge;
  const ticketGuideHref = getGuideHref('troubleshooting', 'sistema-de-tickets-do-xat');

  return (
    <div
      id={`error-${entry.id}`}
      className={cn(
        'rounded-2xl border bg-white transition-[border-color,box-shadow] duration-300',
        isOpen
          ? 'border-blue-200 shadow-md ring-1 ring-blue-100'
          : 'border-gray-200 shadow-sm hover:border-gray-300 hover:shadow'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-gray-50/80 sm:gap-4 sm:px-5 sm:py-4"
        aria-expanded={isOpen}
        aria-controls={`error-panel-${entry.id}`}
      >
        <span
          className={cn(
            'inline-flex shrink-0 items-center rounded-lg border px-2.5 py-1',
            'font-inter text-sm font-semibold tabular-nums tracking-tight',
            'shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]',
            codeTheme.border,
            codeTheme.bg,
            codeTheme.text
          )}
        >
          {entry.codesLabel}
        </span>

        <p className="min-w-0 flex-1 font-inter text-base font-medium leading-snug text-gray-900">
          {entry.title}
        </p>

        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5',
              'font-inter text-xs font-medium whitespace-nowrap',
              severity.badge
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', severity.dot)} aria-hidden />
            {SEVERITY_LABELS[entry.severity]}
          </span>
          <ChevronDown
            className={cn(
              'h-5 w-5 shrink-0 text-gray-400 transition-transform duration-300',
              isOpen && 'rotate-180 text-blue-600'
            )}
            aria-hidden
          />
        </div>
      </button>

      <div
        id={`error-panel-${entry.id}`}
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-6 border-t border-gray-100 px-4 py-5 sm:px-5 sm:py-6">
            <section>
              <h4 className="mb-2 font-poppins text-sm font-bold uppercase tracking-wide text-gray-500">
                O que significa
              </h4>
              <p className="font-inter text-[15px] leading-relaxed text-gray-700">{entry.title}</p>
            </section>

            <section>
              <h4 className="mb-3 font-poppins text-sm font-bold uppercase tracking-wide text-gray-500">
                Como resolver
              </h4>
              <ul className="space-y-2">
                {entry.resolutionSteps.map((step, idx) => (
                  <li
                    key={idx}
                    className="flex gap-3 font-inter text-[15px] leading-relaxed text-gray-700"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h4 className="mb-3 font-poppins text-sm font-bold uppercase tracking-wide text-gray-500">
                Pode acontecer quando
              </h4>
              <div className="flex flex-wrap gap-2">
                {entry.contexts.map((ctx) => (
                  <span
                    key={ctx}
                    className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-inter text-sm text-gray-700"
                  >
                    {ctx}
                  </span>
                ))}
              </div>
            </section>

            {entry.needsTicket ? (
              <section className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <h4 className="mb-2 font-poppins text-sm font-bold text-blue-900">
                  Se ainda não funcionar
                </h4>
                <p className="mb-4 font-inter text-sm leading-relaxed text-blue-800">
                  Este erro costuma exigir ajuda da equipa xat. Abra um ticket com o tópico indicado na
                  resolução acima.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://xat.com/ticket"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 font-inter text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
                  >
                    Abrir ticket
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </a>
                  <Link
                    href={ticketGuideHref}
                    className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2.5 font-inter text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50"
                  >
                    Guia de tickets
                  </Link>
                </div>
              </section>
            ) : null}

            {entry.relatedIds.length > 0 ? (
              <section>
                <h4 className="mb-3 font-poppins text-sm font-bold uppercase tracking-wide text-gray-500">
                  Erros relacionados
                </h4>
                <div className="flex flex-wrap gap-2">
                  {entry.relatedIds.map((relId) => {
                    const related = allEntriesById.get(relId);
                    if (!related) return null;
                    return (
                      <button
                        key={relId}
                        type="button"
                        onClick={() => onOpenRelated(relId)}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-inter text-sm font-semibold text-slate-800 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                      >
                        {related.codesLabel.split(/\s*\/\s*/)[0]?.trim() ?? relId}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function XatErrorsGuide() {
  const { categories, callouts } = useMemo(() => getXatErrorsData(), []);
  const [searchQuery, setSearchQuery] = useState('');
  const [openErrorId, setOpenErrorId] = useState<string | null>(null);

  const normalizedQuery = normalizeSearchQuery(searchQuery);

  const allEntriesById = useMemo(() => {
    const map = new Map<string, XatErrorEntry>();
    for (const category of categories) {
      for (const entry of category.errors) {
        map.set(entry.id, entry);
      }
    }
    return map;
  }, [categories]);

  const filteredCategories = useMemo(() => {
    return categories
      .map((category) => ({
        ...category,
        errors: category.errors.filter((entry) => matchesSearch(entry, normalizedQuery)),
      }))
      .filter((category) => category.errors.length > 0);
  }, [categories, normalizedQuery]);

  const totalVisible = filteredCategories.reduce((sum, c) => sum + c.errors.length, 0);

  const scrollToError = useCallback((id: string) => {
    window.setTimeout(() => {
      const el = document.getElementById(`error-${id}`);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - ERROR_SCROLL_OFFSET;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 320);
  }, []);

  const openErrorWithScroll = useCallback(
    (id: string, updateHash = true) => {
      if (!allEntriesById.has(id)) return;
      setOpenErrorId(id);
      if (updateHash) {
        window.history.replaceState(null, '', `#${id}`);
      }
      scrollToError(id);
    },
    [allEntriesById, scrollToError]
  );

  const handleCategoryNavigate = useCallback((categoryId: XatErrorCategoryId) => {
    const el = document.getElementById(`category-${categoryId}`);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - ERROR_SCROLL_OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && allEntriesById.has(hash)) {
      setOpenErrorId(hash);
      scrollToError(hash);
    }
  }, [allEntriesById, scrollToError]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) {
        setOpenErrorId(null);
        return;
      }
      if (allEntriesById.has(hash)) {
        setOpenErrorId(hash);
        scrollToError(hash);
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [allEntriesById, scrollToError]);

  const handleToggle = useCallback(
    (id: string) => {
      if (openErrorId === id) {
        setOpenErrorId(null);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        return;
      }
      setOpenErrorId(id);
      window.history.replaceState(null, '', `#${id}`);
    },
    [openErrorId]
  );

  return (
    <div className="space-y-14 not-prose">
      <section aria-labelledby="xat-errors-search-heading">
        <h2
          id="xat-errors-search-heading"
          className="mb-4 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Encontrar um erro
        </h2>
        <p className="mb-5 max-w-3xl font-inter text-base leading-relaxed text-gray-600 md:text-lg">
          Pesquise por código (E20, E47…), palavras-chave ou descrição. A lista filtra em tempo real.
        </p>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 sm:left-5 sm:h-6 sm:w-6"
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Ex.: E20, senha, ticket, transferência…"
            className="w-full rounded-2xl border-2 border-gray-200 bg-white py-4 pl-12 pr-12 font-inter text-base text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 sm:py-5 sm:pl-14 sm:text-lg"
            aria-label="Pesquisar erros do xat"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Limpar pesquisa"
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>
        {normalizedQuery ? (
          <p className="mt-3 font-inter text-sm text-gray-500">
            {totalVisible === 0
              ? 'Nenhum erro encontrado para esta pesquisa.'
              : `${totalVisible} ${totalVisible === 1 ? 'erro encontrado' : 'erros encontrados'}`}
          </p>
        ) : null}
      </section>

      {!normalizedQuery ? (
        <section aria-labelledby="xat-errors-categories-heading">
          <h2
            id="xat-errors-categories-heading"
            className="mb-6 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
          >
            Categorias
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((category) => (
              <CategorySummaryCard
                key={category.id}
                category={category}
                onNavigate={handleCategoryNavigate}
              />
            ))}
          </div>
        </section>
      ) : null}

      <div className="space-y-16">
        {filteredCategories.map((category) => {
          const Icon = CATEGORY_ICONS[category.id];
          const theme = CATEGORY_THEMES[category.id];
          return (
            <section
              key={category.id}
              id={`category-${category.id}`}
              className="scroll-mt-28"
              aria-labelledby={`heading-${category.id}`}
            >
              <div className="mb-8 flex items-start gap-4 border-b border-gray-100 pb-6">
                <div
                  className={cn(
                    'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl',
                    theme.card.iconBg,
                    theme.card.iconText
                  )}
                >
                  <Icon
                    className={cn(category.id === 'conta' ? 'h-7 w-7' : 'h-6 w-6')}
                    aria-hidden
                  />
                </div>
                <div>
                  <h2
                    id={`heading-${category.id}`}
                    className="font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
                  >
                    {category.heading}
                  </h2>
                  {category.description ? (
                    <p className="mt-2 max-w-3xl font-inter text-base leading-relaxed text-gray-600">
                      {category.description}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-3">
                {category.errors.map((entry) => (
                  <ErrorAccordionItem
                    key={entry.id}
                    entry={entry}
                    isOpen={openErrorId === entry.id}
                    onToggle={() => handleToggle(entry.id)}
                    onOpenRelated={openErrorWithScroll}
                    allEntriesById={allEntriesById}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {normalizedQuery && totalVisible === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
          <Ban className="mx-auto mb-3 h-8 w-8 text-gray-400" aria-hidden />
          <p className="font-poppins text-lg font-semibold text-gray-900">Nenhum resultado</p>
          <p className="mt-2 font-inter text-sm text-gray-600">
            Tente outro código ou palavra-chave, como «login», «transferência» ou «ticket».
          </p>
        </div>
      ) : null}

      {callouts.length > 0 ? (
        <section
          className="relative overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-slate-50/90 via-white to-amber-50/25 p-6 shadow-sm md:p-8"
          aria-labelledby="xat-errors-tips"
        >
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-200/20 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-20 -left-12 h-40 w-40 rounded-full bg-blue-200/15 blur-3xl"
            aria-hidden
          />

          <div className="relative mb-8 flex flex-col gap-5 border-b border-gray-200/80 pb-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
                <Lightbulb className="h-6 w-6" aria-hidden />
              </div>
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-[0.14em] text-amber-700/90">
                  Leia antes de pedir ajuda
                </p>
                <h2
                  id="xat-errors-tips"
                  className="font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
                >
                  Dicas importantes
                </h2>
                <p className="mt-1.5 max-w-xl font-inter text-sm leading-relaxed text-gray-600 md:text-[15px]">
                  Orientações para abrir tickets, reunir informações e resolver erros com mais
                  rapidez.
                </p>
              </div>
            </div>
            <span className="inline-flex w-fit shrink-0 items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 font-inter text-xs font-semibold text-gray-600 shadow-sm">
              {callouts.length} {callouts.length === 1 ? 'dica' : 'dicas'}
            </span>
          </div>

          <div className="relative grid gap-4 lg:grid-cols-2">
            {callouts.map((callout, idx) => (
              <ErrorCallout key={`${callout.type}-${idx}`} callout={callout} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
